import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';

import { history } from '../../../store';

import { DDNY_MODEL_SCHEMA } from '../../../constants/ddny';

import {
	ddnyTitle,
} from '../../../utils/ddny';
import { getUrlId } from '../../../utils/formatting';

import EditRelatedItems from './blocks/EditRelatedItems';
import EditSectionAttributeBlock from './blocks/EditSectionAttributeBlock';
import EditSectionDataBlock from './blocks/EditSectionDataBlock';
import EditSectionVariants from './blocks/EditSectionVariants';
import LoadingIcon from '../../common/icons/LoadingIcon';
import ProductMedia from './blocks/ProductMedia';
import RhinoConfirm from '../../common/confirm/RhinoConfirm';

import styles from './_styles/DdnyProductEdit.module.scss';

import * as commonActionCreators from '../../../actions/common';
import * as ddnyActionCreators from '../../../actions/ddny';
const allActionCreators = Object.assign({}, commonActionCreators, ddnyActionCreators);

export class DdnyProductEdit extends Component {

	constructor(props) {

		super(props);

		this.productId = getUrlId();
		this.isNew = this.productId === 'new';

		this.state = {

			product: null,

			attributesPending: false,
			
			requestPending: false,
			requestError: null,

			apiVariantData: null,
			apiVariantDataPending: false,

			savePending: false,
			saveError: null,

			inputIsEnabled: true,
			inputSku: '',
			inputNameAlt: '',
			inputDescription: '',
			inputDescriptionShort: '',
			inputBreakdown: '',
			inputFactory: '', 
			inputModelUrl: '', 

			inputWebTitle: '',
			inputWebDescription: '',

			inputVendorStyle: '',
			inputVendorName: '',
			inputVendorNumber: '',

			inputCenterGemstoneType: '',
			inputCenterGemstoneColor: '',
			inputCenterGemstoneWeight: '',
			inputCenterGemstoneCoo: '',
			inputCenterShape: '',

			inputNonCenterDiamondWeight: '',
			inputNonCenterDiamondColor: '',
			inputNonCenterDiamondClarity: '',
			inputNonCenterShape1: '',
			inputNonCenterShape2: '',
			inputNonCenterStoneCount: '',
			inputNonCenterGemstoneType: '',
			inputNonCenterGemstoneWeight: '',

			inputAttributes: {},

			deletePromptOpen: false,
		};

		this.controller = null;
	}

	componentDidMount() {
		this.fetchProduct();
		this.fetchAttributes();
	}

	componentWillUnmount() {
		if(this.controller) {
			this.controller.abort();
		}
	}

	async fetchProduct(config = {}) {

		const showLoading = config.showLoading ?? true;
		
		if(this.isNew === false) {

			if(this.controller) {
				this.controller.abort();
			}
			const controller = new AbortController();
			this.controller = controller;
		
			this.setState({
				apiVariantDataPending: true,
				requestPending: showLoading,
				requestError: null,
			});

			try {

				const initialLoad = this.state.product === null;

				const productData = await this.props.ddnyFetchProductSingle(this.productId, controller.signal);
				this.setState({ product: productData });

				const allFields = Object.values(DDNY_MODEL_SCHEMA).flatMap(section => section.fields);

				const attrObj = {};
				for(const attr of productData.attributes) {
					attrObj[attr.attribute_type] = attr.data_key;
				}

				const stateObj = { inputAttributes: attrObj };
				if(initialLoad) {
					for(const field of allFields) {
						stateObj[field.stateKey] = productData[field.apiKey] || '';
					}
				}

				this.setState(stateObj);
				this.setState({ requestPending: false });

				if(productData.sku && productData.sku.length > 3 && showLoading) {

					const apiData = await this.props.ddnyFetchProductChildData(productData.sku);
	
					if(apiData?.Status === 'success' && apiData?.Data?.data?.length > 0) {
						
						const variantData = apiData?.Data?.data[0].variants?.length > 0 ? apiData?.Data?.data[0].variants : null;
						this.setState({
							apiVariantData: variantData,
						});
					}
				}

			} catch(err) {
				console.error(err);
				this.setState({ requestError: 'Error fetching product' });
			} finally {
				this.setState({
					apiVariantDataPending: false,
					requestPending: false,
				});
			}
		}
	}

	async fetchAttributes() {
		if(this.props.ddny.attributes === null) {
			try {
				this.setState({ attributesPending: true });
				await this.props.ddnyFetchAttributes();
			} catch(err) {
				console.error(err);
			} finally {
				this.setState({ attributesPending: false });
			}
		}
	}

	handleAttributeChange(typeKey, attrValue) {
		this.setState({ inputAttributes: { 
			...this.state.inputAttributes,
			[typeKey]: attrValue || '',
		}});
	}

	handleInputChange(inputKey, inputValue) {
		this.setState({ 
			[inputKey]: inputValue,
		});
	}

	async handleSubmit(evt) {
		if(evt) { evt.preventDefault(); }

		const allFields = Object.values(DDNY_MODEL_SCHEMA).flatMap(section => section.fields);
		
		const apiData = {};
		for(const field of allFields) {
			try {
				if(!field.stateKey) {
					continue;
				}
				if(this.state[field.stateKey].trim && this.state[field.stateKey].trim() === '') {
					continue;
				}
				apiData[field.apiKey] = this.state[field.stateKey];
			} catch(err) {
				console.error(err);
			}
		}

		const attrObj = {};
		for(const attrKey in this.state.inputAttributes) {
			if(this.state.inputAttributes[attrKey]) {
				attrObj[attrKey] = this.state.inputAttributes[attrKey];
			}
		}
		apiData['attributes'] = attrObj;

		try {

			this.setState({
				savePending: true,
				saveError: null,
			});

			const resp = this.isNew
				? await this.props.ddnyFetchProductCreate(apiData)
				: await this.props.ddnyFetchProductUpdate(this.productId, apiData)
			
			if(resp.id) {
				history.push(`/ddny/jewelry/${resp.id || ''}`);
			} else {
				history.push(`/ddny/jewelry/${this.productId || ''}`);
			}

		} catch(err) {
			this.setState({ saveError: err.msg || 'An unexpected error occured' });
		} finally {
			this.setState({ savePending: false });
		}
	}

	async handleDelete() {

		try {

			this.setState({
				requestPending: true, 
				deletePromptOpen: false,
			});

			await this.props.ddnyFetchProductDelete(this.productId)
			
			history.push(`/ddny/gallery`);

		} catch(err) {
			console.error(err);
		} finally {
			this.setState({ requestPending: false });
		}
	}

	render() {
		return <div className={styles.DdnyProductEdit}>
			<div className={styles.editWrapper}>
				{this.state.requestPending 
					?	<div className={styles.loading}>
							<div className={styles.iconWrapper}>
								<LoadingIcon />
							</div>
							<div className={styles.loadingCopy}>Loading Jewelry</div>
						</div>
					:	<form className={styles.loaded} onSubmit={this.handleSubmit.bind(this)}>
							<div className={styles.titleSection}>
								<div className={styles.nameWrapper}>
									<div className={styles.name}>{ddnyTitle({
										name: this.state.inputNameAlt,
										vendor_style: this.state.inputVendorStyle,
									}, 'Untitled Jewelry')}</div>
									<Link 
										to={this.isNew ? `/ddny/admin` : `/ddny/jewelry/${this.productId || ''}`} 
										className={styles.backLink}>
										Back
									</Link>
								</div>
								<div className={styles.sku}>{this.state.product?.sku}</div>
							</div>
							{this.state.saveError && (
								<div className={styles.errorSection}>{this.state.saveError}</div>
							)}
							{!this.isNew && (
								<div className={styles.mediaSection}>
									<ProductMedia
										product={this.state.product}
										readOnly={false}
										refresh={this.fetchProduct.bind(this)} />
								</div>
							)}

							{!this.isNew && (
								<div className={`${styles.dataSection} ${styles.viewSection}`}>
									<div className={styles.title}>Related Items</div>
									<div className={styles.body}>
										<EditRelatedItems 
											product={this.state.product}
											loading={this.state.requestPending}
											refresh={this.fetchProduct.bind(this)} />
									</div>
								</div>
							)}
							
							<div className={`${styles.dataSection} ${styles.viewSection}`}>
								<div className={styles.title}>Product Data</div>
								<div className={styles.body}>
									<EditSectionDataBlock 
										product={this.state.product}
										schema={DDNY_MODEL_SCHEMA['primary']}
										editState={this.state}
										onChange={this.handleInputChange.bind(this)}
										refresh={this.fetchProduct.bind(this)}
										apiData={this.state.apiVariantData}
										apiDataPending={this.state.apiVariantDataPending} />
									<EditSectionDataBlock 
										product={this.state.product}
										schema={DDNY_MODEL_SCHEMA['vendor']}
										editState={this.state}
										onChange={this.handleInputChange.bind(this)}
										refresh={this.fetchProduct.bind(this)}
										apiData={this.state.apiVariantData}
										apiDataPending={this.state.apiVariantDataPending} />
									<EditSectionDataBlock 
										product={this.state.product}
										schema={DDNY_MODEL_SCHEMA['web']}
										editState={this.state}
										onChange={this.handleInputChange.bind(this)}
										refresh={this.fetchProduct.bind(this)}
										apiData={this.state.apiVariantData}
										apiDataPending={this.state.apiVariantDataPending} />
									<EditSectionDataBlock 
										product={this.state.product}
										schema={DDNY_MODEL_SCHEMA['center']}
										editState={this.state}
										onChange={this.handleInputChange.bind(this)}
										refresh={this.fetchProduct.bind(this)}
										apiData={this.state.apiVariantData}
										apiDataPending={this.state.apiVariantDataPending} />
									<EditSectionDataBlock 
										product={this.state.product}
										schema={DDNY_MODEL_SCHEMA['noncenter']}
										editState={this.state}
										onChange={this.handleInputChange.bind(this)}
										refresh={this.fetchProduct.bind(this)}
										apiData={this.state.apiVariantData}
										apiDataPending={this.state.apiVariantDataPending} />
								</div>
							</div>

							<div className={`${styles.attributesSection} ${styles.viewSection}`}>
								<div className={styles.title}>Product Attributes</div>
								<div className={styles.body}>
									{this.state.attributesPending 
										?	<div className={styles.bodyLoading}>
												<div className={styles.bodyIcon}>
													<LoadingIcon />
												</div>
											</div>
										:	<EditSectionAttributeBlock
												product={this.state.product}
												attributes={this.props.ddny.attributes}
												values={this.state.inputAttributes}
												onChange={this.handleAttributeChange.bind(this)} />	
									}
								</div>
							</div>

							{!this.isNew && (
								<div className={`${styles.dataSection} ${styles.viewSection}`}>
									<div className={styles.title}>Product Variants</div>
									<div className={styles.body}>
										<EditSectionVariants 
											product={this.state.product}
											loading={this.state.requestPending}
											refresh={this.fetchProduct.bind(this)} />
									</div>
								</div>
							)}
							
							<div className={styles.actionSection}>
								{!this.state.savePending && (
									<Link to={this.isNew ? `/ddny/admin` : `/ddny/jewelry/${this.productId || ''}`}>
										<button
											type='button'
											className={`${styles.editAction} secondaryButton`}>
											Cancel
										</button>
									</Link>
								)}
								<button
									type='submit'
									disabled={this.state.savePending}
									className={`${styles.editAction} actionButton`}>
									Save
								</button>
								{!this.isNew && (
									<button
										type='button'
										disabled={this.state.savePending}
										className={`${styles.deleteAction} secondaryButton`}
										onClick={() => this.setState({ deletePromptOpen: true })}>
										Delete
									</button>
								)}
							</div>
						</form>
				}
			</div>
			<RhinoConfirm
				confirmTitle='Delete?'
				confirmText='Are you sure you want to delete this jewelry?'
				confirmButtonName={'Delete'}
				confirmClassName={'warning'}
				confirmMethod={this.handleDelete.bind(this)}
				cancelMethod={() => this.setState({ deletePromptOpen: false })}
				open={this.state.deletePromptOpen} />
		</div>;
	}
}

function mapStateToProps(state) {
	return {
		ddny: state.ddny,
	};
}

export default connect(mapStateToProps, allActionCreators)(DdnyProductEdit);